import { type PropsWithChildren, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useLocale } from '../shared/i18n/LocaleContext'
import { flattenMenuItems, isMenuItemActive, menuItems, type MenuItem } from '../shared/navigation/menu'
import type { AuthenticatedUser } from '../features/auth/types'
import { useAuth } from '../features/auth/AuthContext'
import { ThemeSelector } from './ThemeSelector'
import { LanguageSelector } from './LanguageSelector'
import { globalSearch, type GlobalSearchResult } from '../features/search/globalSearchApi'
import { AppFooter } from './AppFooter'
import { AssistantDrawer, type AssistantActivity } from '../features/assistant/AssistantDrawer'
import { getNotifications, getUnreadNotificationCount, markNotificationRead, notificationsChangedEvent } from '../features/notifications/notificationsApi'
import type { NotificationItem } from '../features/notifications/types'
import { formatNotificationMessage } from '../features/notifications/formatNotificationMessage'
import { useAppConfirm } from '../shared/components/AppConfirmDialog'
import { NavigationPreferencesProvider, useNavigationPreferencesState } from '../features/home/NavigationPreferencesContext'
import { AppModal } from '../shared/components/AppModal'

const searchKindLabels = {
  productionOrder: 'menu.productionOrders', article: 'materials.articles',
  mrpForecast: 'mrp.forecasts', mrpRun: 'mrp.runs',
  resource: 'capacity.resources', workCenter: 'capacity.workCenters',
  productionUnit: 'capacity.productionUnits', calendar: 'capacity.calendars',
  warehouse: 'materials.warehouses', maintenanceAsset: 'maintenance.assets',
  maintenancePlan: 'maintenance.plans', maintenanceOrder: 'maintenance.orders',
  qualityInspection:'quality.inspections',qualityIssue:'quality.issues',
  customer:'sales.customers',salesOrder:'sales.orders',supplier:'purchasing.suppliers',purchaseOrder:'purchasing.orders',
  generalLedgerAccount:'finance.accounts',accountingEntry:'finance.entries',
} as const
const searchKindPermissions = {
  productionOrder: 'productionOrders', article: 'materials', resource: 'capacity',
  mrpForecast: 'mrp', mrpRun: 'mrp',
  workCenter: 'capacity', productionUnit: 'capacity', calendar: 'capacity',
  warehouse: 'materials', maintenanceAsset: 'maintenance',
  maintenancePlan: 'maintenance', maintenanceOrder: 'maintenance',
  qualityInspection:'quality',qualityIssue:'quality',
  customer:'sales',salesOrder:'sales',supplier:'purchasing',purchaseOrder:'purchasing',
  generalLedgerAccount:'finance',accountingEntry:'finance',
} as const

export function AppShell({
  children,
  user,
}: PropsWithChildren<{ user: AuthenticatedUser }>) {
  const { t, locale, setLocale, formatDateTime } = useLocale()
  const { logout, selectCulture } = useAuth()
  const { confirm, confirmationDialog } = useAppConfirm()
  const location = useLocation()
  const navigate = useNavigate()
  useEffect(() => {
    const openScenario = async () => {
      if (user.permissions.planning === 'Write' &&
        await confirm(t('planning.changeRequiresSimulation')))
        navigate('/app/planificacion?scenario=global')
    }
    window.addEventListener('apl:planning-scenario-required', openScenario)
    return () => window.removeEventListener('apl:planning-scenario-required', openScenario)
  }, [confirm, navigate, t, user.permissions.planning])
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [recordResults, setRecordResults] = useState<GlobalSearchResult[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [assistantOpen, setAssistantOpen] = useState(false)
  const [assistantActivity, setAssistantActivity] = useState<AssistantActivity>('idle')
  const [unreadNotifications, setUnreadNotifications] = useState(0)
  const [notificationPopupOpen, setNotificationPopupOpen] = useState(false)
  const [notificationPreview, setNotificationPreview] = useState<NotificationItem[]>([])
  const [notificationPreviewLoading, setNotificationPreviewLoading] = useState(false)
  const notificationPopupRef = useRef<HTMLDivElement>(null)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => window.localStorage.getItem('apl-enterprise.sidebar-collapsed') === 'true',
  )
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({})
  const [lockedProduct, setLockedProduct] = useState<MenuItem | null>(null)
  const hasProductionPermission = ['planning', 'mrp', 'productionOrders', 'engineering',
    'capacity', 'maintenance', 'subcontracting', 'reports', 'factoryView']
    .some(code => user.permissions[code] !== 'None')
  const filterVisibleMenuItem = (item: MenuItem): MenuItem | null => {
    if (!user.capabilities.administration && item.product && !user.availableProducts.includes(item.product))
      return { ...item, children: undefined, locked: true }
    if (!user.capabilities.administration && item.product && !user.productLicenses.includes(item.product)) return null
    if (item.product === 'Production' && !hasProductionPermission) return null
    if (item.requiresFactoryView && !user.capabilities.factoryView)
      return { ...item, children: undefined, locked: true }
    if ((item.requiresAplAdministrator && !user.isAplAdministrator) ||
      (item.requiresAdministrator && !user.capabilities.administration) ||
      (item.permissionCode && user.permissions[item.permissionCode] === 'None')) return null
    const children = item.children
      ?.map(filterVisibleMenuItem)
      .filter((child): child is MenuItem => child !== null)
    if (item.children?.length && !children?.length) return null
    return { ...item, children }
  }
  const visibleMenuItems = menuItems
    .map(filterVisibleMenuItem)
    .filter((item): item is MenuItem => item !== null)
  const navigableItems = flattenMenuItems(visibleMenuItems)
  const searchableMenuItems = [
    ...navigableItems,
    { key: 'activeCompany', labelKey: 'user.changeCompany' as const, route: '/app/empresa' },
  ]
  const menuResults = useMemo(() => {
    const normalized = searchQuery.trim().toLocaleLowerCase(locale)
    if (!normalized) return []
    return searchableMenuItems.filter(item =>
      t(item.labelKey).toLocaleLowerCase(locale).includes(normalized),
    )
  }, [locale, searchableMenuItems, searchQuery, t])
  const recordOptions = useMemo(() => recordResults.flatMap(result => {
    const permissionCode = searchKindPermissions[result.kind]
    if (permissionCode && user.permissions[permissionCode] === 'None') return []
    if (result.kind === 'productionOrder') return [{ ...result, option: 'orders.openFullRecord' as const }]
    if (result.kind === 'article') {
      return [{ ...result, route: `/app/materiales/articulos/${result.id}`,
        option: 'capacity.openSection' as const }]
    }
    const canWrite = user.permissions[searchKindPermissions[result.kind]] === 'Write'
    const options: Array<GlobalSearchResult & { option: Parameters<typeof t>[0] }> = canWrite
      ? [{ ...result, route: `${result.route}?edit=${result.id}`, option: 'administration.edit' }]
      : [{ ...result, option: 'capacity.openSection' }]
    return options
  }), [recordResults, user.permissions])
  const currentItem = navigableItems
    .filter((item) =>
      location.pathname === item.route ||
      location.pathname.startsWith(`${item.route}/`),
    )
    .sort((left, right) => right.route.length - left.route.length ||
      Number(Boolean(left.children?.length)) - Number(Boolean(right.children?.length)))[0]
  const currentLabel = location.pathname === '/app/empresa'
    ? t('user.selectCompany')
    : currentItem ? t(currentItem.labelKey) : t('menu.home')
  const navigationPreferences = useNavigationPreferencesState(
    navigableItems, currentItem, `${user.configurationId}:${user.id}`,
  )
  const initials = user.displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
  const toggleSidebar = () => {
    setSidebarCollapsed((current) => {
      const next = !current
      window.localStorage.setItem('apl-enterprise.sidebar-collapsed', String(next))
      return next
    })
  }
  const closeSearch = () => {
    setSearchOpen(false)
    setSearchQuery('')
    setRecordResults([])
    setSearchLoading(false)
  }

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLocaleLowerCase() === 'k') {
        event.preventDefault(); setSearchOpen(true)
      } else if ((event.ctrlKey || event.metaKey) && event.key === '0') {
        event.preventDefault(); closeSearch(); navigate('/app/empresa')
      } else if (event.key === 'Escape') closeSearch()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [navigate])

  useEffect(() => {
    const query = searchQuery.trim()
    if (!searchOpen || query.length < 2) {
      setRecordResults([]); setSearchLoading(false); return
    }
    const controller = new AbortController()
    let active = true
    const timer = window.setTimeout(() => {
      setSearchLoading(true)
      globalSearch(query, controller.signal)
        .then(results => { if (active) setRecordResults(results) })
        .catch(error => {
          if (active && !(error instanceof DOMException && error.name === 'AbortError')) setRecordResults([])
        })
        .finally(() => { if (active) setSearchLoading(false) })
    }, 250)
    return () => { active = false; window.clearTimeout(timer); controller.abort() }
  }, [searchOpen, searchQuery, user.activeCompanyId])

  useEffect(() => {
    if (user.permissions.notifications === 'None') { setUnreadNotifications(0); return }
    let active = true
    const controller = new AbortController()
    const refresh = () => {
      if (document.visibilityState !== 'visible') return
      void getUnreadNotificationCount(controller.signal)
        .then(count => { if (active) setUnreadNotifications(count) })
        .catch(() => undefined)
    }
    const onVisible = () => refresh()
    refresh()
    const timer = window.setInterval(refresh, 30000)
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    window.addEventListener(notificationsChangedEvent, onVisible)
    return () => { active = false; controller.abort(); window.clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('focus', onVisible); window.removeEventListener(notificationsChangedEvent, onVisible) }
  }, [user.activeCompanyId, user.permissions.notifications])

  useEffect(() => {
    if (!notificationPopupOpen) return
    const controller = new AbortController()
    setNotificationPreviewLoading(true)
    void getNotifications({ unreadOnly: true, category: '', page: 1, pageSize: 5 }, controller.signal)
      .then(result => { setNotificationPreview([...result.items]); setUnreadNotifications(result.unreadCount) })
      .catch(() => setNotificationPreview([]))
      .finally(() => setNotificationPreviewLoading(false))
    const close = (event: MouseEvent) => {
      if (!notificationPopupRef.current?.contains(event.target as Node)) setNotificationPopupOpen(false)
    }
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setNotificationPopupOpen(false) }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', escape)
    return () => { controller.abort(); document.removeEventListener('mousedown', close); document.removeEventListener('keydown', escape) }
  }, [notificationPopupOpen, user.activeCompanyId])

  const notificationMessage = (item: NotificationItem) => {
    return formatNotificationMessage(item, t(item.translationKey), locale)
  }
  const readPreviewNotification = async (item: NotificationItem) => {
    await markNotificationRead(item.id)
    setNotificationPreview(current => current.filter(value => value.id !== item.id))
    setUnreadNotifications(current => Math.max(0, current - 1))
  }

  const renderMenuItem = (item: MenuItem, depth = 0): ReactNode => {
    if (item.locked) return <button key={item.key} type="button"
      className="nav-locked-product"
      aria-label={`${t(item.labelKey)} · ${t('licensing.notContracted')}`}
      title={sidebarCollapsed ? `${t(item.labelKey)} · ${t('licensing.notContracted')}` : undefined}
      onClick={() => setLockedProduct(item)}>
      {item.icon && <span className="nav-icon" aria-hidden="true">{item.icon}</span>}
      <span className="nav-label">{t(item.labelKey)}</span>
      <span className="nav-lock" aria-hidden="true">🔒</span>
    </button>
    if (!item.children?.length) return <NavLink
      key={item.key}
      to={item.route}
      end
      onClick={() => setMenuOpen(false)}
      className={() => currentItem?.key === item.key ? 'active' : undefined}
      title={sidebarCollapsed ? t(item.labelKey) : undefined}
    >
      {item.icon && <span className="nav-icon" aria-hidden="true">{item.icon}</span>}
      <span className="nav-label">{t(item.labelKey)}</span>
    </NavLink>

    const active = isMenuItemActive(item, location.pathname)
    const expanded = expandedMenus[item.key] ?? active
    return <div className={`nav-group nav-depth-${depth} ${active ? 'active' : ''}`} key={item.key}>
      <button
        className="nav-group-toggle"
        type="button"
        aria-expanded={expanded}
        title={sidebarCollapsed ? t(item.labelKey) : undefined}
        onClick={() => {
          if (sidebarCollapsed) {
            setSidebarCollapsed(false)
            window.localStorage.setItem('apl-enterprise.sidebar-collapsed', 'false')
            setExpandedMenus(current => ({ ...current, [item.key]: true }))
            return
          }
          setExpandedMenus(current => ({ ...current, [item.key]: !(current[item.key] ?? active) }))
        }}
      >
        {item.icon && <span className="nav-icon" aria-hidden="true">{item.icon}</span>}
        <span className="nav-label">{t(item.labelKey)}</span>
        <svg className="nav-chevron" aria-hidden="true" viewBox="0 0 24 24">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {expanded && <div className="nav-submenu">
        {item.children.map(child => renderMenuItem(child, depth + 1))}
      </div>}
    </div>
  }

  return <NavigationPreferencesProvider value={navigationPreferences}>
    <div className={`app-shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <aside className={`sidebar ${menuOpen ? 'open' : ''}`}>
        <Link to="/app/inicio" className="brand" aria-label="APL Enterprise">
          <img
            className="brand-wordmark"
            src="/apl-enterprise-logo.png"
            alt="APL Enterprise"
          />
          <img
            className="brand-compact"
            src="/apl-enterprise-android-192.png"
            alt=""
            aria-hidden="true"
          />
        </Link>
        <button
          className="sidebar-collapse-toggle"
          type="button"
          aria-label={t(sidebarCollapsed ? 'nav.expandSidebar' : 'nav.collapseSidebar')}
          aria-expanded={!sidebarCollapsed}
          onClick={toggleSidebar}
        >
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>

        <nav className="main-nav" aria-label={t('nav.main')}>
          {visibleMenuItems.map(item => renderMenuItem(item))}
        </nav>

        <div className="sidebar-user">
          <div className="avatar">{initials}</div>
          <div className="sidebar-user-details">
            <strong>{user.displayName}</strong>
            <small>{t(user.licenseKind === 'Administrator'
              ? 'administration.userTypeAdministrator'
              : 'administration.userTypeBasic')}</small>
          </div>
          <button className="logout-button" type="button" onClick={() => void logout()}>
            ↪
          </button>
        </div>
      </aside>

      <div className="workspace">
        <header className="topbar">
          <button
            type="button"
            className="menu-toggle"
            aria-label={t('nav.openMenu')}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((current) => !current)}
          >
            ☰
          </button>
          <div className="breadcrumbs" aria-label={t('nav.breadcrumb')}>
            <Link to="/app/inicio">APL Enterprise</Link>
            <span>›</span>
            <strong>{currentLabel}</strong>
            {currentItem && !currentItem.children?.length && currentItem.key !== 'home' &&
              <button type="button" className={`favorite-screen-button ${navigationPreferences.isFavorite(currentItem.key) ? 'active' : ''}`}
                aria-label={t(navigationPreferences.isFavorite(currentItem.key) ? 'home.removeFavorite' : 'home.addFavorite')}
                title={t(navigationPreferences.isFavorite(currentItem.key) ? 'home.removeFavorite' : 'home.addFavorite')}
                onClick={() => void navigationPreferences.toggleFavorite(currentItem)}>
                {navigationPreferences.isFavorite(currentItem.key) ? '★' : '☆'}
              </button>}
          </div>

          <div className="topbar-actions">
            <button
              type="button"
              className="global-search"
              onClick={() => setSearchOpen(true)}
              aria-label={t('nav.search')}
            >
              <span aria-hidden="true">⌕</span>
            </button>
            <LanguageSelector
              locale={locale}
              label={t('user.language')}
              onChange={(nextLocale) => {
                setLocale(nextLocale)
                void selectCulture(nextLocale)
              }}
            />
            <ThemeSelector compact />
            <div className="notification-popup-anchor" ref={notificationPopupRef}>
              <button className="icon-button" type="button" aria-label={t('menu.notifications')}
                aria-expanded={notificationPopupOpen} aria-haspopup="dialog"
                onClick={() => setNotificationPopupOpen(current => !current)}>
                🔔{unreadNotifications > 0 && <span className="notification-count"
                  title={String(unreadNotifications)}>{unreadNotifications > 99 ? '99+' : unreadNotifications}</span>}
              </button>
              {notificationPopupOpen && <section className="notification-popup" role="dialog" aria-label={t('notifications.latestUnread')}>
                <header><div><strong>{t('notifications.latestUnread')}</strong><small>{unreadNotifications} {t('notifications.unread').toLocaleLowerCase(locale)}</small></div></header>
                <div className="notification-popup-list">
                  {notificationPreviewLoading&&<p className="notification-popup-state">{t('notifications.loading')}</p>}
                  {!notificationPreviewLoading&&notificationPreview.map(item=><article key={item.id}>
                    {item.targetRoute&&(!item.targetPermissionCode||user.permissions[item.targetPermissionCode]!=='None')
                      ? <Link to={item.targetRoute} onClick={()=>{setNotificationPopupOpen(false);if(user.permissions.notifications==='Write')void readPreviewNotification(item)}}><span className={`notification-severity ${item.severity.toLowerCase()}`}/><span><strong>{notificationMessage(item)}</strong><small>{t(`notifications.category.${item.category}`)} · {formatDateTime(item.publishedAtUtc)}</small></span></Link>
                      : <div><span className={`notification-severity ${item.severity.toLowerCase()}`}/><span><strong>{notificationMessage(item)}</strong><small>{t(`notifications.category.${item.category}`)} · {formatDateTime(item.publishedAtUtc)}</small></span></div>}
                    {user.permissions.notifications==='Write'&&<button className="table-link" type="button" onClick={()=>void readPreviewNotification(item)}>{t('notifications.markRead')}</button>}
                  </article>)}
                  {!notificationPreviewLoading&&notificationPreview.length===0&&<p className="notification-popup-state">{t('notifications.noUnread')}</p>}
                </div>
                <footer><Link to="/app/notificaciones" onClick={()=>setNotificationPopupOpen(false)}>{t('notifications.viewAll')}</Link></footer>
              </section>}
            </div>
            <Link className="company-context" to="/app/empresa"
              aria-label={t('user.changeCompany')} title={`${t('user.changeCompany')} · Ctrl + 0`}>
              <span className="company-dot">{user.companies.find(company => company.id === user.activeCompanyId)?.code.slice(0, 1) ?? '—'}</span>
              <span className="company-context-copy">
                <strong>{user.companies.find(company => company.id === user.activeCompanyId)?.code ?? t('user.noCompany')}</strong>
                <small>{user.companies.find(company => company.id === user.activeCompanyId)?.name ?? user.configurationName}</small>
                <em>{t('user.changeCompany')}</em>
              </span>
            </Link>
          </div>
        </header>

        {children}
        <AppFooter />
      </div>

      {searchOpen && (
        <div className="search-overlay" role="presentation" onClick={closeSearch}>
          <section role="dialog" aria-modal="true" aria-label={t('nav.search')} onClick={(event) => event.stopPropagation()}>
            <div className="search-input">
              <span>⌕</span>
              <input autoFocus placeholder={t('nav.search')} value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)} />
              <kbd>Esc</kbd>
            </div>
            <div className="search-suggestions">
              {menuResults.map((item) => (
                <Link to={item.route} key={`menu-${item.key}`} onClick={closeSearch}>
                  <span aria-hidden="true">↗</span><span><strong>{t(item.labelKey)}</strong></span><span>↵</span>
                </Link>
              ))}
              {recordOptions.map(result => <Link to={result.route} key={`${result.kind}-${result.id}-${result.option}`} onClick={closeSearch}>
                <span aria-hidden="true">⌕</span><span><strong>{result.title} · {t(result.option)}</strong><small>{t(searchKindLabels[result.kind])} · {result.description}</small></span><span>↵</span>
              </Link>)}
              {searchLoading&&<div className="search-state">{t('dashboard.loading')}</div>}
              {!searchLoading&&searchQuery.trim()&&menuResults.length===0&&recordOptions.length===0&&<div className="search-state">{t('administration.empty')}</div>}
            </div>
          </section>
        </div>
      )}

      {menuOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label={t('nav.closeMenu')}
          onClick={() => setMenuOpen(false)}
        />
      )}
      {lockedProduct && <AppModal title={t('licensing.productRequired')}
        closeLabel={t('nav.closeMenu')} onClose={() => setLockedProduct(null)}>
        <p>{t('licensing.productRequiredMessage').replace('{product}', t(lockedProduct.labelKey))}</p>
        <div className="form-actions"><button className="primary-button" type="button"
          onClick={() => setLockedProduct(null)}>{t('administration.close')}</button></div>
      </AppModal>}
      {user.capabilities.assistant && user.permissions.assistant !== 'None' && !assistantOpen && <button
        className={`assistant-launcher assistant-launcher-floating ${assistantActivity}`}
        type="button" aria-label={t('assistant.open')} title={t('assistant.open')}
        onClick={() => { setAssistantOpen(true); setAssistantActivity(current => current === 'completed' ? 'idle' : current) }}>
        <AssistantIcon />{assistantActivity !== 'idle' && <span className="assistant-launcher-status" aria-hidden="true" />}
      </button>}
      {user.capabilities.assistant && user.permissions.assistant !== 'None' &&
        <AssistantDrawer open={assistantOpen} onClose={() => setAssistantOpen(false)} onActivityChange={setAssistantActivity} />}
      {confirmationDialog}
    </div>
  </NavigationPreferencesProvider>
}

function AssistantIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path className="assistant-icon-main" d="M12 2.6c.48 3.46 2.45 5.43 5.9 5.9-3.45.48-5.42 2.45-5.9 5.9-.48-3.45-2.45-5.42-5.9-5.9 3.45-.47 5.42-2.44 5.9-5.9Z" />
    <path className="assistant-icon-star" d="M18.35 13.5c.2 1.45 1.03 2.28 2.48 2.48-1.45.2-2.28 1.03-2.48 2.48-.2-1.45-1.03-2.28-2.48-2.48.2-1.45 1.03-2.28 2.48-2.48ZM5.25 13.85c.14 1.04.74 1.64 1.78 1.78-1.04.15-1.64.74-1.78 1.79-.15-1.05-.74-1.64-1.79-1.79 1.05-.14 1.64-.74 1.79-1.78Z" />
  </svg>
}
